import { PieceType, DEFAULT_CONFIG, PlacedPiece } from '@game/engine';

interface TileInfoProps {
  pieceType: PieceType;
  piece?: PlacedPiece;
  enhancementInfo?: {
    multiplier: number;
    enhancerCount: number;
  };
  tax?: number;
  simple?: boolean;
}

const PIECE_DESCRIPTIONS: Record<PieceType, string> = {
  O: 'Generates currency.',
  I: 'Increases adjacent elements\' effects.',
  L: 'Reduces adjacent elements\' cooldown.',
  S: 'Increases max currency capacity.',
  T: 'Spreads bonus effects.',
};

export function TileInfo({ pieceType, piece, enhancementInfo, tax, simple }: TileInfoProps) {
  const config = DEFAULT_CONFIG.tileConfigs[pieceType];
  const description = PIECE_DESCRIPTIONS[pieceType];

  const enhancementMultiplier = enhancementInfo?.multiplier ?? 0;
  const level = piece?.level ?? 1;
  const upgradePerLevel = config.upgradeEffectPerLevel ?? 0;

  // Returns the leveled base value for a given config base
  const leveledBase = (base: number) => base + (level - 1) * upgradePerLevel;

  let effectLine = '';
  let activeLine = '';
  let cooldownLine = '';

  // Effect line (generation, enhancement, reduction, or storage)
  if (config.generationRate > 0) {
    const base = leveledBase(config.generationRate);
    const enhanced = base * (1 + enhancementMultiplier);
    const taxAmount = tax ?? 0;
    const actual = Math.max(0, enhanced - taxAmount);
    const bonusAmount = enhanced - base;
    if (simple) {
      effectLine = `Effect: ${Math.round(actual)}💰/s`;
    } else if (taxAmount > 0) {
      effectLine = `Effect: ${Math.round(actual)}💰/s (base: ${Math.round(base)}💰/s, bonus: +${Math.round(bonusAmount)}💰/s, 🏛 -${Math.round(taxAmount)}💰/s)`;
    } else {
      effectLine = `Effect: ${Math.round(actual)}💰/s (base: ${Math.round(base)}💰/s, bonus: ${bonusAmount > 0 ? '+' : ''}${Math.round(bonusAmount)}💰/s)`;
    }
  } else if (config.enhancerMultiplier && config.enhancerMultiplier > 0) {
    const base = leveledBase(config.enhancerMultiplier);
    effectLine = `Effect: +${Math.round(base * 100)}%`;
  } else if (config.cooldownReduction && config.cooldownReduction > 0) {
    const base = leveledBase(config.cooldownReduction);
    const actual = base * (1 + enhancementMultiplier);
    const bonusAmount = actual - base;
    effectLine = simple
      ? `Effect: ${Math.round(actual)}s`
      : `Effect: ${Math.round(actual)}s (base: ${Math.round(base)}s, bonus: ${bonusAmount > 0 ? '+' : ''}${Math.round(bonusAmount)}s)`;
  } else if (config.storageBonus && config.storageBonus > 0) {
    const base = leveledBase(config.storageBonus);
    const actual = base * (1 + enhancementMultiplier);
    const bonusAmount = actual - base;
    effectLine = simple
      ? `Effect: +${Math.round(actual)}💰`
      : `Effect: +${Math.round(actual)}💰 (base: +${Math.round(base)}💰, bonus: ${bonusAmount > 0 ? '+' : ''}${Math.round(bonusAmount)}💰)`;
  }

  // Active line
  const activeBase = config.activeDuration;
  if (activeBase === 0) {
    activeLine = `Active: ∞`;
  } else {
    activeLine = `Active: ${activeBase}s`;
  }

  // Cooldown line
  const cooldownBase = config.cooldownDuration;
  const cooldownBonus = piece?.cooldownDuration !== undefined ? piece.cooldownDuration - cooldownBase : 0;
  cooldownLine = simple
    ? `Cooldown: ${cooldownBase + cooldownBonus}s`
    : `Cooldown: ${cooldownBase + cooldownBonus}s (base: ${cooldownBase}s, bonus: ${cooldownBonus > 0 ? '+' : ''}${cooldownBonus}s)`;

  return (
    <div style={{ fontSize: '13px' }}>
      <div><strong>{pieceType}</strong></div>
      <div>{description}</div>
      <div style={{ height: '1em' }}></div>
      <div>Cost: {config.cost}💰</div>
      <div>{effectLine}</div>
      <div>{activeLine}</div>
      <div>{cooldownLine}</div>
    </div>
  );
}
